import React, { useState, useEffect } from 'react';
import Dialog from './Dialog';
import TextInput from './TextInput';
import SearchableSelect from './SearchableSelect';
import styles from './FilterDialog.module.css';

/**
 * Reusable FilterDialog Component
 * 
 * Usage Example:
 * const filterFields = [
 *   { type: 'text', name: 'name', label: 'Name', placeholder: 'Enter name...' },
 *   { type: 'select', name: 'category', label: 'Category', options: [{ value: '1', label: 'Option 1' }] },
 *   { type: 'date', name: 'startDate', label: 'Start Date' },
 *   { type: 'number', name: 'amount', label: 'Amount', placeholder: 'Enter amount...' }
 * ];
 * 
 * <FilterDialog
 *   isOpen={showDialog}
 *   onClose={() => setShowDialog(false)}
 *   onApply={(filterValues) => console.log(filterValues)}
 *   title="Filter Options"
 *   fields={filterFields}
 *   initialValues={currentFilters}
 * />
 */
const FilterDialog = ({
    isOpen,
    onClose,
    onApply,
    title = "Filter Options",
    fields = [],
    initialValues = {}
}) => {
    const [filterValues, setFilterValues] = useState({});

    useEffect(() => {
        if (isOpen) {
            // Initialize with provided initial values or appropriate defaults based on field type
            const initValues = {};
            fields.forEach(field => {
                if (field.type === 'searchable-select') {
                    // For searchable-select, keep the full object or null
                    initValues[field.name] = initialValues[field.name] || null;
                } else {
                    // For other fields, use empty string
                    initValues[field.name] = initialValues[field.name] || '';
                }
            });
            setFilterValues(initValues);
        }
    }, [isOpen, fields, initialValues]);

    const handleInputChange = (fieldName, value) => {
        setFilterValues(prev => ({
            ...prev,
            [fieldName]: value
        }));
    };

    const handleCancel = () => {
        const resetValues = {};
        fields.forEach(field => {
            if (field.type === 'searchable-select') {
                resetValues[field.name] = initialValues[field.name] || null;
            } else {
                resetValues[field.name] = initialValues[field.name] || '';
            }
        });
        setFilterValues(resetValues);
        onClose();
    };

    const handleApply = () => {
        onApply(filterValues);
        onClose();
    };

    const handleClearAll = () => {
        const resetValues = {};
        fields.forEach(field => {
            if (field.type === 'searchable-select') {
                resetValues[field.name] = null;
            } else {
                resetValues[field.name] = '';
            }
        });
        setFilterValues(resetValues);
    };

    const hasValues = Object.entries(filterValues).some(([key, value]) => {
        const field = fields.find(f => f.name === key);
        if (field?.type === 'searchable-select') {
            // For searchable-select, check if we have a valid object
            return value && typeof value === 'object' && Object.keys(value).length > 0;
        } else {
            // For other fields, check if value exists and is not empty string
            return value && value.toString().trim() !== '';
        }
    });

    const renderField = (field) => {
        const { type, name, label, placeholder, options } = field;

        switch (type) {
            case 'text':
                return (
                    <TextInput
                        key={name}
                        label={label}
                        placeholder={placeholder}
                        value={filterValues[name] || ''}
                        onChange={(e) => handleInputChange(name, e.target.value)}
                    />
                );

            case 'select':
                return (
                    <div key={name} className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>{label}</label>
                        <select
                            className={styles.selectInput}
                            value={filterValues[name] || ''}
                            onChange={(e) => handleInputChange(name, e.target.value)}
                        >
                            <option value="">Select {label}</option>
                            {options.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                );

            case 'searchable-select':
                return (
                    <SearchableSelect
                        key={name}
                        label={label}
                        placeholder={placeholder}
                        value={
                            filterValues[name] && typeof filterValues[name] === 'object'
                                ? filterValues[name][field.valueKey] || filterValues[name].id || filterValues[name]._id
                                : filterValues[name] || ''
                        }
                        onChange={(value, option) => {
                            // For searchable-select, store the full option object if available, 
                            // otherwise store null for empty selections
                            handleInputChange(name, option || null);
                        }}
                        onSearch={field.onSearch}
                        options={field.searchOptions || []}
                        loading={field.loading || false}
                        displayKey={field.displayKey || 'name'}
                        valueKey={field.valueKey || '_id'}
                    />
                );

            case 'date':
                return (
                    <div key={name} className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>{label}</label>
                        <input
                            type="date"
                            className={styles.dateInput}
                            value={filterValues[name] || ''}
                            onChange={(e) => handleInputChange(name, e.target.value)}
                            placeholder={placeholder}
                        />
                    </div>
                );

            case 'number':
                return (
                    <div key={name} className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>{label}</label>
                        <input
                            type="number"
                            className={styles.numberInput}
                            value={filterValues[name] || ''}
                            onChange={(e) => handleInputChange(name, e.target.value)}
                            placeholder={placeholder}
                        />
                    </div>
                );

            default:
                return null;
        }
    };

    console.log('FilterDialog render - isOpen:', isOpen, 'fields:', fields);

    return (
        <Dialog open={isOpen} onClose={handleCancel} title={title}>
            <div className={styles.filterContent}>
                <div className={styles.fieldsContainer}>
                    {fields.map(field => renderField(field))}

                    {hasValues && (
                        <button
                            type="button"
                            className={styles.clearAllButton}
                            onClick={handleClearAll}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                            Clear All Fields
                        </button>
                    )}
                </div>

                <div className={styles.buttonGroup}>
                    <button
                        type="button"
                        className={styles.cancelButton}
                        onClick={handleCancel}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={styles.applyButton}
                        onClick={handleApply}
                    >
                        Apply Filter
                    </button>
                </div>
            </div>
        </Dialog>
    );
};

export default FilterDialog;
